import firebase from "firebase";
import Promise from "bluebird";
import moment from "moment";
import firebaseApp from "../config/firebase";

import {
  USER_ACTION_TYPES,
  CREATE_STRIPE_CUSTOMER_URL,
  UPDATE_CUSTOMER_URL,
  CREATE_STRIPE_CUSTOMER_SUBSCRIPTION_URL,
  UPDATE_STRIPE_CUSTOMER_SUBSCRIPTION_URL,
  CANCEL_SUBSCRIPTION_URL,
  ADD_PAYMENT_SOURCE_URL,
  FETCH_COUPON,
} from "./constants";

import { handleException } from "../app";
import { postRequest, getRequest } from "../../RequestUtil";

import { isMobile, getOneSignalInfo, setAuthToken } from "../../helpers/Utils";

import type {
  Action,
  Dispatch,
  GetState,
  Id,
  UpdateUserObject,
} from "../types";

const FIREBASE_ERROR_CODES = {
  INCORRECT_PASSWORD: "auth/wrong-password",
  INVALID_EMAIL: "auth/invalid-email",
  WEAK_PASSWORD: "auth/weak-password",
  UNAVAILABLE_EMAIL: "auth/email-already-in-use",
};

type RegisterCredentialsObject = {
  email: string,
  password: string,
  firstName: string,
  lastName: string,
};

type LoginCredentialsObject = {
  email: string,
  password: string,
};

export function register(
  credentials: RegisterCredentialsObject
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: USER_ACTION_TYPES.REGISTER_USER,
    });
    try {
      const { email, password } = credentials;
      const auth = firebaseApp.auth();
      auth.onAuthStateChanged(() => dispatch(handleAuthStateChange()));
      await auth.createUserWithEmailAndPassword(email, password);
      const currentUser = firebase.auth().currentUser;
      const db = firebaseApp.database();
      const user = db.ref(`/users/${currentUser.uid}`);
      const { code } = credentials;
      const { firstName } = credentials;
      const { lastName } = credentials;
      var { timezone } = credentials;
      await user.set({
        email,
        firstName,
        lastName,
        notificationsEnabled: true,
        isPremium: false,
        timezone,
      });
      dispatch(
        login({
          email,
          password,
        })
      );
    } catch (error) {
      switch (error.code) {
        case FIREBASE_ERROR_CODES.INVALID_EMAIL:
          dispatch({
            type: USER_ACTION_TYPES.RECEIVE_INVALID_EMAIL,
          });
          break;
        case FIREBASE_ERROR_CODES.WEAK_PASSWORD:
          dispatch({
            type: USER_ACTION_TYPES.RECEIVE_WEAK_PASSWORD,
          });
          break;
        case FIREBASE_ERROR_CODES.UNAVAILABLE_EMAIL:
          dispatch({
            type: USER_ACTION_TYPES.RECEIVE_UNAVAILABLE_EMAIL,
          });
          break;
        default:
          dispatch(handleException(error));
      }
    }
  };
}

export function registerUserAndAddToProject(
  credentials: RegisterCredentialsObject
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: USER_ACTION_TYPES.REGISTER_TEMP_USER,
    });
    try {
      const { email, password } = credentials;
      const auth = firebaseApp.auth();
      auth.onAuthStateChanged(() => dispatch(handleAuthStateChange()));
      await auth.createUserWithEmailAndPassword(email, password);
      const currentUser = firebase.auth().currentUser;
      const db = firebaseApp.database();
      const user = db.ref(`/users/${currentUser.uid}`);
      const { code } = credentials;
      const { firstName } = credentials;
      const { lastName } = credentials;
      await user.set({
        email,
        firstName,
        lastName,
        notificationsEnabled: true,
        isPremium: true,
      });
      dispatch({
        type: USER_ACTION_TYPES.RECEIVE_TEMP_USER,
        payload: {
          tempUser: {
            id: user.id,
          },
        },
      });
    } catch (error) {
      switch (error.code) {
        case FIREBASE_ERROR_CODES.INVALID_EMAIL:
          dispatch({
            type: USER_ACTION_TYPES.RECEIVE_INVALID_EMAIL,
          });
          break;
        case FIREBASE_ERROR_CODES.WEAK_PASSWORD:
          dispatch({
            type: USER_ACTION_TYPES.RECEIVE_WEAK_PASSWORD,
          });
          break;
        default:
          dispatch(handleException(error));
      }
    }
  };
}

export function setupAuth(): (
  dispatch: Dispatch,
  getState: GetState
) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: USER_ACTION_TYPES.SETUP_AUTH,
    });
    try {
      const auth = firebaseApp.auth();
      auth.onAuthStateChanged(() => dispatch(handleAuthStateChange()));
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function login(
  credentials: LoginCredentialsObject
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: USER_ACTION_TYPES.FETCH_USER,
    });
    try {
      const { email, password } = credentials;
      const auth = firebaseApp.auth();
      auth.onAuthStateChanged(() => dispatch(handleAuthStateChange()));
      await auth.signInWithEmailAndPassword(email, password);

      // To help with skipping the login page every time the site loads.
      setAuthToken(auth);

      // Save OneSignal info for notifications
      if (isMobile()) saveOneSignalId();
    } catch (error) {
      console.log(error);
      switch (error.code) {
        case FIREBASE_ERROR_CODES.INCORRECT_PASSWORD:
          dispatch({
            type: USER_ACTION_TYPES.RECEIVE_INCORRECT_PASSWORD,
          });
          break;
        case FIREBASE_ERROR_CODES.INVALID_EMAIL:
          dispatch({
            type: USER_ACTION_TYPES.RECEIVE_INVALID_EMAIL,
          });
          break;
        default:
          dispatch(handleException(error));
      }
    }
  };
}

export function clearIncorrectPasswordError(): (
  dispatch: Dispatch,
  getState: GetState
) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: USER_ACTION_TYPES.CLEAR_INCORRECT_PASSWORD,
      alertText: 'This doesn"t alert',
    });
  };
}

export function clearInvalidEmailError(): (
  dispatch: Dispatch,
  getState: GetState
) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: USER_ACTION_TYPES.CLEAR_INVALID_EMAIL,
    });
  };
}

export function clearWeakPasswordError(): (
  dispatch: Dispatch,
  getState: GetState
) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: USER_ACTION_TYPES.CLEAR_WEAK_PASSWORD,
    });
  };
}

function handleAuthStateChange() {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({ type: USER_ACTION_TYPES.AUTH_STATE_CHANGE });
    const auth = firebase.auth();
    const db = firebase.database();
    if (!auth.currentUser) {
      dispatch({
        type: USER_ACTION_TYPES.RECEIVE_LOGOUT,
      });
      return;
    }

    // To help with skipping the login page every time the site loads.
    // Placing here also incase the token doesn't exist for any reason but
    // firebase thinks they're logged in.
    setAuthToken(auth);
    const { uid, email } = auth.currentUser;
    const userRef = db.ref(`users/${uid}`);
    await userRef.on("value", (user) => {
      if (!user) {
        dispatch({
          type: USER_ACTION_TYPES.RECEIVE_LOGOUT,
        });
      }
      dispatch({
        type: USER_ACTION_TYPES.RECEIVE_USER,
        payload: {
          user: {
            ...auth.currentUser.toJSON(),
            ...user.toJSON(),
          },
        },
      });
    });
  };
}

export function logout(): (dispatch: Dispatch) => Promise<Action<void>> {
  return async (dispatch: Dispatch) => {
    // To help with skipping the login page every time the site loads.
    setAuthToken();

    dispatch({
      type: USER_ACTION_TYPES.FETCH_LOGOUT,
    });
    try {
      const auth = firebaseApp.auth();
      await auth.signOut();
      return dispatch({
        type: USER_ACTION_TYPES.RECEIVE_LOGOUT,
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function sendPasswordResetEmail(
  email: string
): (dispatch: Dispatch) => Promise<Action<void>> {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: USER_ACTION_TYPES.RESET_PASSWORD,
    });
    try {
      const auth = firebaseApp.auth();
      await auth.sendPasswordResetEmail(email);
      return dispatch({
        type: USER_ACTION_TYPES.RECEIVE_RESET_PASSWORD,
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

type UploadAvatarParams = {
  userId: string,
  uri: string,
};

const uploadAvatar = async ({ userId, uri }: UploadAvatarParams) => {
  if (uri) {
    const storage = firebase.storage();
    const ref = storage.ref().child(`avatars/${userId}`);
    const blob = new Blob([uri], { type: uri.type });
    const upload = await ref.put(blob, { contentType: uri.type });
    const downloadURL = await upload.ref.getDownloadURL();
    return downloadURL;
  } else {
    return null;
  }
};

export function toggleNotifications(
  enabled: boolean
): (dispatch: Dispatch) => Promise<Action<void>> {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: USER_ACTION_TYPES.TOGGLE_NOTIFICATIONS,
    });
    try {
      const auth = firebaseApp.auth();
      const userId = auth.currentUser.uid;
      const db = firebaseApp.database();
      const notificationsRef = db.ref(`users/${userId}/notificationsEnabled`);
      await notificationsRef.set(enabled);
      dispatch({
        type: USER_ACTION_TYPES.RECEIVE_TOGGLED_NOTIFICATIONS,
        payload: {
          enabled,
        },
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function loadUser(id: Id): (dispatch: Dispatch) => Promise<void> {
  return async (dispatch: Dispatch) => {
    dispatch({ type: USER_ACTION_TYPES.FETCH_USER_BY_ID });
    try {
      const db = firebaseApp.database();
      const userRef = db.ref("users").child(id);
      const user = await userRef.once("value");
      dispatch({
        type: USER_ACTION_TYPES.RECEIVE_USER_BY_ID,
        payload: {
          user: user.toJSON(),
        },
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function updateUser(
  params: UpdateUserObject
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: USER_ACTION_TYPES.UPDATE_USER,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const userId = auth.currentUser.uid;
      const user = {
        name: params.name,
        firstName: params.firstName,
        lastName: params.lastName,
        email: currentUser.email,
        avatarURL: params.avatarURI,
        isPremium: params.isPremium,
        notificationsEnabled: params.notificationsEnabled,
        code: currentUser.code ? currentUser.code : "",
        timezone: params.timezone,
      };
      await db.ref(`users/${userId}`).update(user);
      const userRef = selectUserWithId(userId);
      const value = await db.ref(`users/${userId}`).once("value");

      if (params.avatarURI != params.avatarURL) {
        const avatarURL = await uploadAvatar({
          userId: userId,
          uri: params.avatarURI,
        });

        await userRef.child("avatarURL").set(avatarURL);
      }
      dispatch({
        type: USER_ACTION_TYPES.RECEIVE_UPDATED_USER,
        payload: {
          user: value.toJSON(),
        },
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

const selectUserWithId = (user: Id) =>
  firebaseApp.database().ref("users").child(user);

export function createCustomer(
  token: string
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: USER_ACTION_TYPES.CREATE_CUSTOMER,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      await postRequest({
        url: CREATE_STRIPE_CUSTOMER_URL,
        query: {
          email: currentUser.email,
          uid: currentUser.uid,
          token: token.token,
        },
      });
      dispatch({
        type: USER_ACTION_TYPES.RECEIVE_CUSTOMER,
        payload: {},
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function updateCustomer(
  params: string
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: USER_ACTION_TYPES.UPDATE_CUSTOMER,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      await postRequest({
        url: UPDATE_CUSTOMER_URL,
        query: {
          uid: currentUser.uid,
          token: params.token,
          customerId: params.customerId,
        },
      });
      dispatch({
        type: USER_ACTION_TYPES.RECEIVE_UPDATED_CUSTOMER,
        payload: {},
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function addPaymentSource(
  params: string
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: USER_ACTION_TYPES.ADD_PAYMENT_SOURCE,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      await postRequest({
        url: ADD_PAYMENT_SOURCE_URL,
        query: {
          uid: currentUser.uid,
          customerId: params.customerId,
          token: params.token,
        },
      });
      dispatch({
        type: USER_ACTION_TYPES.RECEIVE_PAYMENT_SOURCE,
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function createCustomerSubscription(
  params: string
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: USER_ACTION_TYPES.CREATE_SUBSCRIPTION,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      await postRequest({
        url: CREATE_STRIPE_CUSTOMER_SUBSCRIPTION_URL,
        query: {
          uid: currentUser.uid,
          customerId: params.customerId,
          planId: params.planId,
          coupon: params.coupon,
        },
      });
      dispatch({
        type: USER_ACTION_TYPES.RECEIVE_SUBSCRIPTION,
      });
    } catch (error) {
      console.log(error);
      dispatch(handleException(error));
    }
  };
}

export function updateCustomerSubscription(
  params: string
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: USER_ACTION_TYPES.UPDATE_SUBSCRIPTION,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      await postRequest({
        url: UPDATE_STRIPE_CUSTOMER_SUBSCRIPTION_URL,
        query: {
          uid: currentUser.uid,
          subscriptionId: params.subscriptionId,
          planId: params.planId,
        },
      });
      dispatch({
        type: USER_ACTION_TYPES.RECEIVE_UPDATED_SUBSCRIPTION,
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function cancelSubscription(
  params: string
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: USER_ACTION_TYPES.CANCEL_SUBSCRIPTION,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      await postRequest({
        url: CANCEL_SUBSCRIPTION_URL,
        query: {
          uid: currentUser.uid,
          subscriptionId: params.subscriptionId,
        },
      });
      dispatch({
        type: USER_ACTION_TYPES.RECEIVE_UPDATED_SUBSCRIPTION,
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function fetchCoupon(
  coupon: string
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: USER_ACTION_TYPES.FETCH_COUPON,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      await fetch(FETCH_COUPON + "?coupon=" + coupon.coupon, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }).then((response) => {
        if (response.ok) {
          response.json().then((json) => {
            if (json.error) {
              dispatch({
                type: USER_ACTION_TYPES.RECEIVE_INCORRECT_COUPON,
                payload: {
                  coupon: json,
                },
              });
            } else {
              dispatch({
                type: USER_ACTION_TYPES.RECEIVE_COUPON,
                payload: {
                  coupon: json,
                },
              });
            }
          });
        }
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function saveOneSignalId(): Promise<void> {
  return (async () => {
    const getExpireTime = () => moment().add(365, "days");

    try {
      const oneSignalId = getOneSignalInfo().oneSignalInfo.oneSignalUserId;

      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const userId = auth.currentUser.uid;

      const devicesRef = db.ref(`users/${userId}/devices`);
      const oldDevices = await devicesRef
        .once("value")
        .then((snapshot) => snapshot.val());
      let newDevices = [];
      if (oldDevices && oldDevices.length) newDevices = JSON.parse(oldDevices);
      if (!newDevices.map((dev) => dev.deviceId).includes(oneSignalId))
        newDevices.push({
          deviceId: oneSignalId,
          expires: getExpireTime(),
        });
      else
        newDevices = [
          ...newDevices.map((dev) => ({
            ...dev,
            expires: getExpireTime(),
          })),
        ];

      await devicesRef.set(JSON.stringify(newDevices));
    } catch (error) {
      console.log(error);
    }
  })();
}



// WEBPACK FOOTER //
// ./src/redux/user/actions.js